import { OnDataOptions, useSubscription } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Subscriptions } from '@shared/api';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { MBView } from '@shared/components/MBView';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import {
  BulkUpdateProvider,
  ControlsProvider,
  ViewTemplateProvider,
} from '@shared/components/filter/state';
import { MBIconV2 } from '@shared/components/image';
import { LoadingIndicator } from '@shared/components/loading';
import { UpgradeModalProvider } from '@shared/scenes/billing';
import { OpenEntityOptions } from '@shared/scenes/cards/collectionView/hooks';
import { CardChannelProvider } from '@shared/scenes/cards/context';
import { CardList } from '@shared/scenes/cards/listView/components/CardList';
import { useCardListView } from '@shared/scenes/cards/listView/hooks/CardListViewHooks';
import { useCountWorkflowEntities } from '@shared/scenes/dashboard/hooks/useCountWorkflowEntities';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { ReferencedInComponent } from '@shared/scenes/workflows/config';
import {
  WorkspaceDetail,
  useViewTemplate,
  useWorkflowTemplate,
  useWorkspaceDetails,
} from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { RouteName, RouteParamMapping, useNavigator } from '@shared/util/navigation';
import { notNil } from '@shared/util/notNil';
import { omitTypeName } from '@shared/util/omitTypeName';
import { ChannelPermissionsProvider } from '@shared/util/permissions';
import { AddMoreButton } from './AddMoreButton';
import { EditFieldContainer } from './EditFieldContainer';

const COUNT_REFETCH_TIMEOUT_MS = 500;

interface ReferencedInEditFieldProps {
  component: ReferencedInComponent;
  entityId: string;
  style?: CSSProperties;
}

export function ReferencedInEditField({ component, entityId, style }: ReferencedInEditFieldProps) {
  const colors = useContext(ColorThemeContext);
  const [showingList, setShowingList] = useState(false);
  const {
    workspaceDetails,
    countLoading,
    workflowDetailsLoading,
    referencedWorkflow,
    count,
    viewTemplate,
    navigateToCreate,
  } = useWorkflowReferencedInComponent(component, entityId);

  const workspaceName =
    component.label ??
    workspaceDetails.get(referencedWorkflow?.id ?? '')?.name ??
    referencedWorkflow?.name;
  const workspaceId = workspaceDetails.get(referencedWorkflow?.id ?? '')?.id ?? 0;
  const workspaceImageUrl =
    workspaceDetails.get(referencedWorkflow?.id ?? '')?.imageUrl ??
    referencedWorkflow?.channelDefaultConfiguration?.imageUrl;

  const isMisconfigured =
    !component.referencedFromWorkflowTemplateId ||
    !component.relatedCardComponentId ||
    !component.referencingViewTemplateId;
  const isReadOnly = isMisconfigured;

  const loading = countLoading || workflowDetailsLoading;
  const showCount = !countLoading && !isEmpty(workspaceName);
  const printContent =
    !countLoading && count != null && count > 0
      ? i18n('card.blocks.inCards.cards', { count, max: 10000 })
      : undefined;

  const anchorName = getAnchorName('referenced-in', workspaceId);

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      label={i18n('workflowTemplate.component.type.label.referencedIn')}
      iconUrl={workspaceImageUrl}
      onPress={isReadOnly ? undefined : () => setShowingList(prev => !prev)}
      style={style}
      isGrayScale={isMisconfigured}
      printContent={printContent}
      subfooter={
        <WarningSubfooter
          showWarning={isMisconfigured}
          message={i18n('workflowTemplate.component.error.misconfigured')}
        />
      }
      footer={
        <AddMoreButton
          component={component}
          onPress={navigateToCreate}
          label={i18n('workflowTemplate.component.referencedIn.createCard')}
        />
      }
    >
      <MBView row paddingVertical={1} style={styles.field}>
        <View>
          <Text size="body1">{workspaceName ?? ' '}</Text>
          <Text size="body2">
            {!showCount && ' '}
            {showCount && i18n('card.blocks.inCards.cards', { count: count ?? 0, max: 10000 })}
          </Text>
        </View>
        {loading && <LoadingIndicator paddingHorizontal={0.5} />}
        {!loading && (
          <MBIconV2 name="arrow-point-right" size={grid(2.5)} color={colors.foregroundActive} />
        )}
      </MBView>
      {showingList && !isNil(referencedWorkflow) && (
        <AnchoredSheet
          anchorName={anchorName}
          onClose={() => setShowingList(false)}
          initialSize={'full'}
          title={workspaceName ?? ''}
          style={{ minHeight: grid(28) }}
        >
          <MBView style={styles.listContainer}>
            <ReferencedWorkflowCollectionView
              workflowTemplate={referencedWorkflow}
              viewTemplate={viewTemplate}
              baseNavigationPath={workspaceDetails.get(referencedWorkflow?.id)?.baseNavigationPath}
              component={component}
              workspaceId={workspaceId}
              entityId={entityId}
              closeSheet={() => setShowingList(false)}
            />
          </MBView>
        </AnchoredSheet>
      )}
    </EditFieldContainer>
  );
}

interface ReferencedWorkflowCollectionViewProps {
  workflowTemplate: API.WorkflowTemplateFragment;
  viewTemplate?: API.ViewTemplateFragment;
  component: ReferencedInComponent;
  closeSheet: () => void;
  baseNavigationPath?: string;
  workspaceId: number;
  entityId: string;
}

function ReferencedWorkflowCollectionView({
  workflowTemplate,
  viewTemplate,
  component,
  baseNavigationPath,
  workspaceId,
  entityId,
  closeSheet,
}: ReferencedWorkflowCollectionViewProps) {
  if (isNil(viewTemplate)) {
    return (
      <View padding={2}>
        <Text size="body1" style={{ textAlign: 'center' }}>
          {i18n('builder.referencedInOptions.noView')}
        </Text>
      </View>
    );
  }

  // Exclude any existing filter for relatedCardComponentId from the view template so the
  // entityId filter below is never overridden by a duplicate entry at a later position.
  const existingFilters = getFiltersFromViewTemplate(viewTemplate).filter(
    f => f.componentId !== component.relatedCardComponentId,
  );

  const viewTemplateWithFilter: API.ViewTemplateFragment = {
    ...viewTemplate,
    collectionCriteria: {
      filterByComponents: [
        {
          componentId: component.relatedCardComponentId,
          value: {
            containsCurrentUser: null,
            containsNumber: null,
            containsString: { values: [entityId], negate: false },
            dateIs: null,
            dateRange: null,
            dateTokenIs: null,
            isEmpty: null,
            numberIs: null,
            stringWildcard: null,
          },
        },
        ...existingFilters,
      ],
      sortByComponents: viewTemplate?.collectionCriteria?.sortByComponents?.map(omitTypeName) ?? [],
      groups: [],
    },
  };
  return (
    <ChannelPermissionsProvider channelId={workspaceId}>
      <ViewTemplateProvider
        key={`${viewTemplateWithFilter.id}-${entityId}`}
        workflowTemplateId={workflowTemplate.id}
        viewTemplate={viewTemplateWithFilter}
      >
        <BulkUpdateProvider
          viewTemplate={viewTemplateWithFilter}
          workflowTemplateId={workflowTemplate.id}
          channelId={workspaceId}
        >
          <ControlsProvider workflowTemplateId={workflowTemplate.id}>
            <UpgradeModalProvider>
              <ReferencedWorkflowListView
                workflowTemplate={workflowTemplate}
                baseNavigationPath={baseNavigationPath ?? ''}
                viewTemplate={viewTemplate}
                workspaceId={workspaceId}
                closeSheet={closeSheet}
              />
            </UpgradeModalProvider>
          </ControlsProvider>
        </BulkUpdateProvider>
      </ViewTemplateProvider>
    </ChannelPermissionsProvider>
  );
}

interface ReferencedWorkflowListViewProps {
  workflowTemplate: API.WorkflowTemplateFragment;
  baseNavigationPath: string;
  viewTemplate: API.ViewTemplateFragment;
  workspaceId: number;
  closeSheet: () => void;
}

function ReferencedWorkflowListView({
  workflowTemplate,
  baseNavigationPath,
  viewTemplate,
  workspaceId,
  closeSheet,
}: ReferencedWorkflowListViewProps) {
  const {
    entities,
    openEntity,
    getIsCardBeforePaidCutoff,
    hasMoreCards,
    fetchMore,
    isMoreCardsLoading,
    isCardsLoading,
    canUpdateCard,
  } = useCardListView(workflowTemplate?.id, viewTemplate?.id, workspaceId, baseNavigationPath);
  const openEntityAndClose = useCallback(
    (options: OpenEntityOptions) => {
      openEntity({
        ...options,
        backTooltip: i18n('card.navigation.returnToReferencedCard'),
      });
      closeSheet();
    },
    [closeSheet, openEntity],
  );

  if (canUpdateCard) {
    return (
      <CardChannelProvider channelSlug={baseNavigationPath ?? ''}>
        <View paddingVertical={1} paddingHorizontal={0.5}>
          <CardList
            canUpdateCard={canUpdateCard}
            workflowTemplate={workflowTemplate}
            viewTemplate={viewTemplate}
            entities={entities}
            openEntity={openEntityAndClose}
            getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
            hasMore={hasMoreCards}
            showMore={fetchMore}
            isMoreLoading={isMoreCardsLoading}
            isCardsLoading={isCardsLoading}
            isGroup={false}
            hasOverflowMenu={false}
          />
        </View>
      </CardChannelProvider>
    );
  }
  return (
    <View paddingVertical={1} paddingHorizontal={0.5}>
      <CardList
        canUpdateCard={canUpdateCard}
        workflowTemplate={workflowTemplate}
        viewTemplate={viewTemplate}
        entities={entities}
        openEntity={openEntityAndClose}
        getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
        hasMore={hasMoreCards}
        showMore={fetchMore}
        isMoreLoading={isMoreCardsLoading}
        isCardsLoading={isCardsLoading}
        isGroup={false}
        hasOverflowMenu={false}
      />
    </View>
  );
}

function useWorkflowReferencedInComponent(
  component: ReferencedInComponent,
  entityId: string,
): {
  workspaceDetails: Map<string, WorkspaceDetail>;
  countLoading: boolean;
  workflowDetailsLoading: boolean;
  viewTemplate?: API.ViewTemplateFragment;
  viewTemplateLoading: boolean;
  referencedWorkflow?: API.WorkflowTemplateFragment;
  navigateToCreate: () => void;
  count?: number;
} {
  const referencedFromWorkflowTemplate = useWorkflowTemplate(
    component.referencedFromWorkflowTemplateId,
  );
  const navigator = useNavigator();
  const workspaceDetails = useWorkspaceDetails();

  const { viewTemplate, loading: viewTemplateLoading } = useViewTemplate(
    component.referencedFromWorkflowTemplateId,
    component.referencingViewTemplateId,
  );

  const navigateToCreate = useCallback(() => {
    const navigationPath = workspaceDetails.get(
      referencedFromWorkflowTemplate.workflowTemplate?.id ?? '',
    )?.baseNavigationPath;
    if (!isNil(navigationPath)) {
      const navEndpoint: RouteName = 'CardCreate';
      const navOptions: RouteParamMapping['CardCreate'] = {
        channelSlug: navigationPath,
        fieldOverrides: {
          [component.relatedCardComponentId]: [{ id: entityId }],
        },
      };

      navigator.navigate(navEndpoint, navOptions);
    }
  }, [
    entityId,
    component.relatedCardComponentId,
    navigator,
    referencedFromWorkflowTemplate.workflowTemplate?.id,
    workspaceDetails,
  ]);

  const input = useMemo(() => {
    // Exclude any existing filter for relatedCardComponentId so the entityId filter isn't
    // overridden by a duplicate entry coming from the view template.
    const existingFiltersForCount = getFiltersFromViewTemplate(viewTemplate).filter(
      f => f.componentId !== component.relatedCardComponentId,
    );
    return {
      workflowTemplateId: component.referencedFromWorkflowTemplateId,
      filterByComponents: [
        {
          componentId: component.relatedCardComponentId,
          value: {
            containsCurrentUser: null,
            containsNumber: null,
            containsString: { values: [entityId], negate: false },
            dateIs: null,
            dateRange: null,
            dateTokenIs: null,
            isEmpty: null,
            numberIs: null,
            stringWildcard: null,
          },
        },
        ...existingFiltersForCount,
      ],
    };
  }, [
    component.referencedFromWorkflowTemplateId,
    component.relatedCardComponentId,
    entityId,
    viewTemplate,
  ]);

  const [canFetchCount, setCanFetchCount] = useState(false);
  const {
    totalResults,
    loading: countLoading,
    refetch: refetchCount,
  } = useCountWorkflowEntities({
    input,
    skip: isNil(viewTemplate) || !canFetchCount,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const indexDelay = setTimeout(() => {
      setCanFetchCount(true);
    }, COUNT_REFETCH_TIMEOUT_MS);
    return () => clearTimeout(indexDelay);
  }, []);

  useSubscription<API.WorkflowEntitySubscription>(Subscriptions.workflow.event, {
    onData: useCallback(
      ({ data }: OnDataOptions<API.WorkflowEntitySubscription>) => {
        if (isNil(data.data) || !canFetchCount) {
          return;
        }
        const { type, entities } = data.data.event;
        const hasRelevantEntity = entities.some(e =>
          isReferencedInEntity({ entity: e, eventType: type, component, entityId }),
        );
        if (hasRelevantEntity) {
          refetchCount();
        }
      },
      [canFetchCount, component, entityId, refetchCount],
    ),
  });

  return {
    workspaceDetails,
    countLoading: countLoading || !canFetchCount,
    workflowDetailsLoading: referencedFromWorkflowTemplate.loading,
    referencedWorkflow: referencedFromWorkflowTemplate.workflowTemplate,
    count: totalResults,
    viewTemplate,
    viewTemplateLoading,
    navigateToCreate,
  };
}

function getFiltersFromViewTemplate(
  viewTemplate?: API.ViewTemplateFragment,
): API.FilterByComponentObject[] {
  return (
    viewTemplate?.collectionCriteria?.filterByComponents?.filter(notNil).map(omitTypeName) ?? []
  );
}

function isReferencedInEntity({
  entity,
  eventType,
  component,
  entityId,
}: {
  entity: API.WorkflowEntitySubscription['event']['entities'][number];
  eventType: string;
  component: ReferencedInComponent;
  entityId: string;
}): boolean {
  if (entity.workflowTemplateId !== component.referencedFromWorkflowTemplateId) {
    return false;
  }
  // For new cards, only refetch if the card actually references the current
  // entity — avoids unnecessary refetches when unrelated cards are created
  // in the same workflow.
  if (eventType === 'WORKFLOW_ENTITY_CREATED') {
    const relatedField = entity.fields?.[component.relatedCardComponentId];
    return Array.isArray(relatedField) && relatedField.some(item => item.id === entityId);
  }
  // For updates/deletes the reference may have just been removed, so
  // refetch conservatively rather than risk a stale count.
  return true;
}

const styles = StyleSheet.create({
  container: {
    minHeight: grid(5),
    paddingHorizontal: grid(3),
    paddingVertical: grid(2),
    justifyContent: 'space-between',
  },
  workflowImage: {
    height: grid(5),
    width: grid(5),
    borderRadius: grid(2.5),
    marginRight: 9,
  },
  textContainer: {
    paddingLeft: grid(1),
    flex: 1,
  },
  plusButton: {
    alignSelf: 'flex-end',
  },
  listContainer: {
    width: 400,
    maxHeight: 600,
    minHeight: 100,
    flex: 1,
  },
  field: {
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingRight: grid(1),
  },
});
